import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { NAV, CONTACT } from "@/data/site";
import { EASE } from "@/components/motion";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const light = scrolled;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          light
            ? "bg-white/95 backdrop-blur-md border-b border-mist text-ink"
            : "bg-transparent text-white"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-[1600px] items-center justify-between px-6 lg:px-12">
          <Link to="/" data-testid="header-logo" className="flex items-center" aria-label="Urban Trail home">
            <img
              src={light ? "/assets/logo-dark.png" : "/assets/logo-white.png"}
              alt="Urban Trail"
              className="h-6 w-auto transition-opacity duration-500"
            />
          </Link>

          <nav className="hidden items-center gap-10 lg:flex" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `group relative py-2 text-[11px] font-medium uppercase tracking-[0.24em] transition-colors duration-300 ${
                    isActive ? "" : light ? "text-graphite hover:text-ink" : "text-white/80 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span
                      className={`absolute -bottom-0.5 left-0 h-px bg-gold transition-all duration-500 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-plan-journey"
              className={`ml-4 border px-6 py-3 text-[11px] font-medium uppercase tracking-[0.24em] transition-colors duration-500 ${
                light
                  ? "border-ink/40 text-ink hover:bg-ink hover:text-white"
                  : "border-white/50 text-white hover:bg-white hover:text-ink"
              }`}
            >
              Plan Your Journey
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            className="flex flex-col gap-1.5 p-2 lg:hidden"
            aria-label="Open menu"
          >
            <span className={`block h-px w-7 ${light ? "bg-ink" : "bg-white"}`} />
            <span className={`block h-px w-7 ${light ? "bg-ink" : "bg-white"}`} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] flex flex-col bg-ink text-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex h-20 items-center justify-between px-6">
              <img src="/assets/logo-white.png" alt="Urban Trail" className="h-6 w-auto" />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="p-2 text-[11px] uppercase tracking-[0.3em] text-white/80"
                aria-label="Close menu"
              >
                Close
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 + i * 0.07, ease: EASE }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className="block py-3 font-serif text-4xl font-light tracking-tight text-white/90 transition-colors hover:text-gold"
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
                className="pt-8"
              >
                <Link to="/contact" data-testid="mobile-nav-plan-journey" className="btn-outline-light">
                  Plan Your Journey
                </Link>
              </motion.div>
            </nav>
            <div className="px-8 pb-10 text-xs tracking-[0.2em] text-white/50 uppercase">
              {CONTACT.email}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
